/**
 * CaptainDashboard — the daily workflow surface for Matt + Michelle.
 *
 * Styled as an Airbus A320 EFIS / glass-cockpit panel (Capt. Matt flies the
 * bus): dark glareshield header, black "screen" panels with bezels, the
 * Airbus readout palette (green / cyan / amber / magenta on black), monospace
 * type, annunciator-style status pills, and a live Zulu clock.
 *
 * Cosmetic only — all data wiring (/api/admin/me, bookings, leads, blockouts),
 * handlers, and data-testid hooks are unchanged.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';

import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';

// ── A320 EFIS palette ──────────────────────────────────────────────────────
const EFIS = {
  page: '#0a0d11',
  panel: '#05080b',
  row: '#0c1117',
  bezel: '#2c333b',
  bezelHi: '#3f4954',
  green: '#27d36a',
  cyan: '#1ec8ff',
  magenta: '#ff5fd2',
  amber: '#ffb020',
  white: '#e3eaf1',
  dim: '#79858f',
  red: '#ff5c5c',
  mono: "'SFMono-Regular', 'Consolas', 'Liberation Mono', 'Menlo', monospace",
};

export default function CaptainDashboard() {
  const { user, signOut } = useAuth();
  const [me, setMe] = useState(null);
  const [meError, setMeError] = useState(null);
  const [meLoading, setMeLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setMeLoading(true);
    api
      .get('/api/admin/me')
      .then((res) => {
        if (cancelled) return;
        setMe(res.data?.user || null);
        setMeError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        const message =
          err?.response?.data?.error ||
          err?.message ||
          'Unable to verify session with server.';
        setMeError(message);
      })
      .finally(() => {
        if (!cancelled) setMeLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const displayName =
    me?.profile?.full_name || user?.email?.split('@')[0] || 'Captain';
  const displayRole = me?.profile?.role;

  return (
    <>
      <Helmet>
        <title>Captain Dashboard — Nowhere Charters</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <main
        style={{
          minHeight: '100vh',
          background: `radial-gradient(1200px 600px at 50% -10%, #11161c 0%, ${EFIS.page} 60%)`,
          color: EFIS.white,
          fontFamily: EFIS.mono,
          padding: '1.25rem 1.25rem 4rem',
        }}
      >
        <div style={{ maxWidth: '1120px', margin: '0 auto' }}>
          {/* ── Glareshield / FCU header ── */}
          <header
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: '1rem',
              flexWrap: 'wrap',
              marginBottom: '1.5rem',
              padding: '1rem 1.15rem',
              background: 'linear-gradient(180deg, #161b21 0%, #0c1015 100%)',
              border: `1px solid ${EFIS.bezel}`,
              borderRadius: '12px',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: '0.62rem',
                  letterSpacing: '0.22em',
                  color: EFIS.dim,
                  textTransform: 'uppercase',
                  marginBottom: '0.35rem',
                }}
              >
                ◆ Nowhere Charters — A320 Captain EFIS
              </div>
              <h1
                style={{
                  margin: 0,
                  fontSize: '1.45rem',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  color: EFIS.green,
                  textShadow: `0 0 12px ${EFIS.green}55`,
                }}
              >
                {meLoading
                  ? 'CAPTAIN DASHBOARD'
                  : `WELCOME, ${displayName.toUpperCase()}`}
              </h1>
              <p
                style={{
                  margin: '0.35rem 0 0',
                  fontSize: '0.82rem',
                  color: EFIS.cyan,
                  letterSpacing: '0.04em',
                }}
                data-testid="captain-email"
              >
                {user?.email}
                {displayRole ? (
                  <span style={{ color: EFIS.dim }}>
                    {'  ·  '}
                    {displayRole.toUpperCase()}
                  </span>
                ) : null}
              </p>
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.85rem',
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
              }}
            >
              <span
                style={{
                  padding: '0.3rem 0.6rem',
                  border: `1px solid ${EFIS.bezelHi}`,
                  borderRadius: '6px',
                  color: EFIS.white,
                  fontSize: '0.82rem',
                  fontWeight: 700,
                  letterSpacing: '0.14em',
                  background: '#0c1015',
                }}
              >
                NC&middot;320
              </span>
              <ZuluClock />
              <button
                type="button"
                onClick={signOut}
                data-testid="signout-button"
                style={{
                  padding: '0.5rem 0.9rem',
                  background: '#1a1207',
                  color: EFIS.amber,
                  border: `1px solid ${EFIS.amber}88`,
                  borderRadius: '6px',
                  fontFamily: EFIS.mono,
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  cursor: 'pointer',
                }}
              >
                SIGN OUT
              </button>
            </div>
          </header>

          {/* ── ECAM-style caution line ── */}
          {meError ? (
            <div
              role="alert"
              data-testid="me-error"
              style={{
                marginBottom: '1.5rem',
                padding: '0.75rem 1rem',
                background: '#1a0e0e',
                color: EFIS.red,
                border: `1px solid ${EFIS.red}66`,
                borderRadius: '8px',
                fontSize: '0.85rem',
                letterSpacing: '0.04em',
              }}
            >
              <strong style={{ letterSpacing: '0.12em' }}>SERVER CHECK FAIL</strong>{' '}
              — {meError}
            </div>
          ) : null}

          {/* ── EFIS screens ── */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))',
              gap: '1.25rem',
            }}
          >
            <BookingsPanel />
            <LeadsPanel />
            <AvailabilityPanel />
          </div>
        </div>
      </main>
    </>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Live Zulu clock (pilots run on UTC) — purely decorative, real time.
// ───────────────────────────────────────────────────────────────────────────
function ZuluClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const zulu = now.toISOString().slice(11, 19);
  const local = now.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return (
    <div
      style={{
        padding: '0.3rem 0.6rem',
        border: `1px solid ${EFIS.bezel}`,
        borderRadius: '6px',
        background: '#05080b',
        fontSize: '0.82rem',
        letterSpacing: '0.06em',
        whiteSpace: 'nowrap',
      }}
      title="Zulu (UTC) · Local"
    >
      <span style={{ color: EFIS.green, textShadow: `0 0 8px ${EFIS.green}55` }}>
        {zulu}
      </span>
      <span style={{ color: EFIS.dim }}>Z · {local} LCL</span>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Shared screen chrome
// ───────────────────────────────────────────────────────────────────────────
function ScreenTitle({ children, accent, sub, right }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: '0.6rem',
        marginBottom: '0.9rem',
        paddingBottom: '0.55rem',
        borderBottom: `1px solid ${EFIS.bezel}`,
      }}
    >
      <span style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', minWidth: 0 }}>
        <span
          style={{
            color: accent,
            fontSize: '0.88rem',
            fontWeight: 700,
            letterSpacing: '0.2em',
            textShadow: `0 0 10px ${accent}44`,
          }}
        >
          {children}
        </span>
        {sub ? (
          <span
            style={{
              color: EFIS.dim,
              fontSize: '0.6rem',
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
            }}
          >
            {sub}
          </span>
        ) : null}
      </span>
      {right || null}
    </div>
  );
}

const screenStyle = {
  background: EFIS.panel,
  border: `1px solid ${EFIS.bezel}`,
  borderRadius: '10px',
  padding: '1.1rem 1.15rem 1.25rem',
  boxShadow: 'inset 0 0 38px rgba(0,0,0,0.55), 0 1px 0 rgba(255,255,255,0.03)',
};

// ───────────────────────────────────────────────────────────────────────────
// DEPARTURES — bookings (fetches /api/admin/bookings).
// ───────────────────────────────────────────────────────────────────────────
function BookingsPanel() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(
    () =>
      api
        .get('/api/admin/bookings')
        .then((res) => {
          setBookings(res.data?.bookings || []);
          setError(null);
        })
        .catch((err) => {
          setError(
            err?.response?.data?.error ||
              err?.message ||
              'Could not load bookings.',
          );
        })
        .finally(() => {
          setLoading(false);
        }),
    [],
  );

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section data-testid="bookings-panel" style={screenStyle}>
      <ScreenTitle accent={EFIS.green} sub="upcoming charters">
        DEPARTURES
      </ScreenTitle>

      {loading ? (
        <p style={panelTextStyle}>ACQUIRING…</p>
      ) : error ? (
        <p style={{ ...panelTextStyle, color: EFIS.red }}>{error}</p>
      ) : bookings.length === 0 ? (
        <p style={panelTextStyle}>
          No upcoming departures. Confirmed charters show here as soon as
          customers reserve, or you add one manually.
        </p>
      ) : (
        <ul style={listStyle}>
          {bookings.map((b) => (
            <BookingRow key={b.id} booking={b} onChanged={load} />
          ))}
        </ul>
      )}
    </section>
  );
}

function BookingRow({ booking, onChanged }) {
  const tripName = booking.trip?.name || 'Trip';
  const customer = booking.customer;
  const customerName = customer
    ? `${customer.first_name} ${customer.last_name}`.trim()
    : 'Unknown customer';
  const date = formatDate(booking.charter_date);
  const time = formatTime(booking.start_time);

  // null | 'cancel' | 'archive'
  const [acting, setActing] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const isCancelled = booking.booking_status === 'cancelled';

  const run = async (action) => {
    setBusy(true);
    setErr(null);
    try {
      await api.post(`/api/admin/bookings/${booking.id}/${action}`, {
        reason: reason.trim() || undefined,
      });
      if (onChanged) await onChanged();
    } catch (e) {
      setErr(
        e?.response?.data?.error ||
          e?.message ||
          `Could not ${action} this booking.`,
      );
      setBusy(false);
    }
  };

  return (
    <li style={rowStyle}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '0.3rem',
          gap: '0.5rem',
        }}
      >
        <span style={{ fontWeight: 700, color: EFIS.cyan, letterSpacing: '0.04em' }}>
          {tripName}
        </span>
        <StatusPill status={booking.booking_status} />
      </div>
      <div style={{ fontSize: '0.92rem', marginBottom: '0.3rem', color: EFIS.white }}>
        {customerName}
        {customer?.phone ? (
          <>
            {'  ·  '}
            <a href={`tel:${customer.phone}`} style={linkStyle}>
              {customer.phone}
            </a>
          </>
        ) : null}
      </div>
      <div style={{ fontSize: '0.8rem', color: EFIS.dim, letterSpacing: '0.04em' }}>
        {date} · {time} · {booking.guest_count} PAX
        {booking.booking_number ? ` · #${booking.booking_number}` : null}
      </div>

      {acting === null ? (
        <div style={{ marginTop: '0.6rem', display: 'flex', gap: '0.5rem' }}>
          {!isCancelled ? (
            <button
              type="button"
              data-testid="booking-cancel"
              style={rowActionStyle(EFIS.amber)}
              onClick={() => {
                setReason('');
                setErr(null);
                setActing('cancel');
              }}
            >
              Cancel
            </button>
          ) : null}
          <button
            type="button"
            data-testid="booking-archive"
            style={rowActionStyle(EFIS.dim)}
            onClick={() => {
              setErr(null);
              setActing('archive');
            }}
          >
            Archive
          </button>
        </div>
      ) : acting === 'cancel' ? (
        <div style={{ marginTop: '0.6rem' }}>
          <input
            type="text"
            value={reason}
            maxLength={200}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (optional) — weather, customer request…"
            data-testid="booking-cancel-reason"
            style={rowInputStyle}
          />
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.4rem' }}>
            <button
              type="button"
              disabled={busy}
              data-testid="booking-cancel-confirm"
              style={rowActionStyle(EFIS.red)}
              onClick={() => run('cancel')}
            >
              {busy ? '…' : 'Confirm cancel'}
            </button>
            <button
              type="button"
              disabled={busy}
              style={rowActionStyle(EFIS.dim)}
              onClick={() => setActing(null)}
            >
              Back
            </button>
          </div>
        </div>
      ) : (
        <div style={{ marginTop: '0.6rem' }}>
          <p style={{ ...panelTextStyle, margin: '0 0 0.4rem' }}>
            Archive removes this from the board (kept on record). Continue?
          </p>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              type="button"
              disabled={busy}
              data-testid="booking-archive-confirm"
              style={rowActionStyle(EFIS.amber)}
              onClick={() => run('archive')}
            >
              {busy ? '…' : 'Confirm archive'}
            </button>
            <button
              type="button"
              disabled={busy}
              style={rowActionStyle(EFIS.dim)}
              onClick={() => setActing(null)}
            >
              Back
            </button>
          </div>
        </div>
      )}

      {err ? (
        <p style={{ ...panelTextStyle, color: EFIS.red, marginTop: '0.4rem' }}>
          {err}
        </p>
      ) : null}
    </li>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// INBOUND — leads (fetches /api/admin/leads).
// ───────────────────────────────────────────────────────────────────────────
function LeadsPanel() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get('/api/admin/leads')
      .then((res) => {
        if (cancelled) return;
        setLeads(res.data?.leads || []);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err?.response?.data?.error || err?.message || 'Could not load leads.',
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section data-testid="leads-panel" style={screenStyle}>
      <ScreenTitle accent={EFIS.amber} sub="new leads">
        INBOUND
      </ScreenTitle>

      {loading ? (
        <p style={panelTextStyle}>ACQUIRING…</p>
      ) : error ? (
        <p style={{ ...panelTextStyle, color: EFIS.red }}>{error}</p>
      ) : leads.length === 0 ? (
        <p style={panelTextStyle}>
          No inbound traffic. Form submissions from nowherechartersllc.com
          appear here.
        </p>
      ) : (
        <ul style={listStyle}>
          {leads.map((lead) => (
            <LeadRow key={lead.id} lead={lead} />
          ))}
        </ul>
      )}
    </section>
  );
}

function LeadRow({ lead }) {
  const fullName = [lead.first_name, lead.last_name].filter(Boolean).join(' ');
  const displayName = fullName || lead.email || 'Unnamed lead';
  const sourceLabel = SOURCE_LABEL[lead.source] || lead.source;

  return (
    <li style={rowStyle}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '0.3rem',
          gap: '0.5rem',
        }}
      >
        <span style={{ fontWeight: 700, color: EFIS.white, letterSpacing: '0.04em' }}>
          {displayName}
        </span>
        <LeadStatusPill status={lead.status} />
      </div>
      <div style={{ fontSize: '0.88rem', marginBottom: '0.3rem' }}>
        {lead.email ? (
          <a href={`mailto:${lead.email}`} style={linkStyle}>
            {lead.email}
          </a>
        ) : null}
        {lead.phone ? (
          <>
            {'  ·  '}
            <a href={`tel:${lead.phone}`} style={linkStyle}>
              {lead.phone}
            </a>
          </>
        ) : null}
      </div>
      {lead.message ? (
        <div
          style={{
            fontSize: '0.83rem',
            color: EFIS.white,
            opacity: 0.8,
            fontStyle: 'italic',
            marginBottom: '0.35rem',
          }}
        >
          &ldquo;{lead.message}&rdquo;
        </div>
      ) : null}
      <div style={{ fontSize: '0.76rem', color: EFIS.dim, letterSpacing: '0.06em' }}>
        {String(sourceLabel).toUpperCase()} · {formatRelative(lead.created_at)}
      </div>
    </li>
  );
}

const SOURCE_LABEL = {
  'wp-contact-form': 'Contact form',
  'wp-first-time-guide': 'First-Time Guide',
  manual: 'Manual entry',
  'wp-unknown': 'Website',
};

const LEAD_STATUS_PALETTE = {
  new: { fg: EFIS.amber, label: 'NEW' },
  contacted: { fg: EFIS.cyan, label: 'CONTACTED' },
  converted: { fg: EFIS.green, label: 'CONVERTED' },
  archived: { fg: EFIS.dim, label: 'ARCHIVED' },
};

function LeadStatusPill({ status }) {
  const palette = LEAD_STATUS_PALETTE[status] || LEAD_STATUS_PALETTE.new;
  return <Annunciator fg={palette.fg}>{palette.label}</Annunciator>;
}

function formatRelative(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMin = Math.round((Date.now() - then) / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

// ── EFIS annunciator pill (boxed, colored, monospace) ──
function Annunciator({ fg, children }) {
  return (
    <span
      style={{
        fontSize: '0.66rem',
        fontWeight: 700,
        fontFamily: EFIS.mono,
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        padding: '0.18rem 0.5rem',
        borderRadius: '4px',
        color: fg,
        border: `1px solid ${fg}77`,
        background: `${fg}14`,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

function StatusPill({ status }) {
  const fg = STATUS_PALETTE[status] || STATUS_PALETTE.default;
  return <Annunciator fg={fg}>{String(status || 'unknown')}</Annunciator>;
}

const STATUS_PALETTE = {
  confirmed: EFIS.green,
  in_progress: EFIS.amber,
  completed: EFIS.cyan,
  cancelled: EFIS.red,
  canceled: EFIS.red,
  no_show: EFIS.red,
  default: EFIS.dim,
};

// ───────────────────────────────────────────────────────────────────────────
// NOTAM — availability / blockouts (list / add / delete).
// ───────────────────────────────────────────────────────────────────────────
const BLOCKOUT_REASONS = [
  { value: 'personal', label: 'Personal / flying schedule' },
  { value: 'maintenance', label: 'Boat maintenance' },
  { value: 'weather', label: 'Weather' },
  { value: 'other', label: 'Other' },
];

const REASON_LABEL = Object.fromEntries(
  BLOCKOUT_REASONS.map((r) => [r.value, r.label]),
);

function AvailabilityPanel() {
  const [blockouts, setBlockouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = React.useCallback(() => {
    setLoading(true);
    return api
      .get('/api/admin/blockouts')
      .then((res) => {
        setBlockouts(res.data?.blockouts || []);
        setError(null);
      })
      .catch((err) => {
        setError(
          err?.response?.data?.error ||
            err?.message ||
            'Could not load availability.',
        );
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAdded = () => {
    setAdding(false);
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this blockout? Bookings will be allowed again on these dates.')) {
      return;
    }
    setDeletingId(id);
    try {
      await api.delete(`/api/admin/blockouts/${id}`);
      setBlockouts((prev) => prev.filter((b) => b.id !== id));
    } catch (err) {
      alert(err?.response?.data?.error || err?.message || 'Delete failed.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <section data-testid="availability-panel" style={screenStyle}>
      <ScreenTitle
        accent={EFIS.cyan}
        sub="availability · blockouts"
        right={
          <button
            type="button"
            onClick={() => setAdding(true)}
            data-testid="add-blockout-button"
            style={{
              padding: '0.3rem 0.7rem',
              background: '#06212b',
              color: EFIS.cyan,
              border: `1px solid ${EFIS.cyan}77`,
              borderRadius: '5px',
              fontFamily: EFIS.mono,
              fontSize: '0.72rem',
              fontWeight: 700,
              letterSpacing: '0.1em',
              cursor: 'pointer',
            }}
          >
            + BLOCK OUT
          </button>
        }
      >
        NOTAM
      </ScreenTitle>

      <p style={{ ...panelTextStyle, fontSize: '0.78rem', marginBottom: '0.8rem' }}>
        Days are bookable by default. File a blockout for any day or range
        you&apos;re off-station.
      </p>

      {loading ? (
        <p style={panelTextStyle}>ACQUIRING…</p>
      ) : error ? (
        <p style={{ ...panelTextStyle, color: EFIS.red }}>{error}</p>
      ) : blockouts.length === 0 ? (
        <p style={panelTextStyle}>
          No active NOTAMs. You&apos;re open for booking on every date.
        </p>
      ) : (
        <ul style={{ ...listStyle, gap: '0.5rem' }}>
          {blockouts.map((b) => (
            <BlockoutRow
              key={b.id}
              blockout={b}
              busy={deletingId === b.id}
              onDelete={() => handleDelete(b.id)}
            />
          ))}
        </ul>
      )}

      {adding ? (
        <AddBlockoutModal
          onClose={() => setAdding(false)}
          onAdded={handleAdded}
        />
      ) : null}
    </section>
  );
}

function BlockoutRow({ blockout, busy, onDelete }) {
  const sameDay = blockout.start_date === blockout.end_date;
  const range = sameDay
    ? formatDate(blockout.start_date)
    : `${formatDate(blockout.start_date)} → ${formatDate(blockout.end_date)}`;
  const reasonLabel = REASON_LABEL[blockout.reason] || blockout.reason;

  return (
    <li
      style={{
        ...rowStyle,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '0.75rem',
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: '0.92rem', color: EFIS.amber }}>
          {range}
        </div>
        <div style={{ fontSize: '0.78rem', color: EFIS.dim }}>
          {reasonLabel}
          {blockout.notes ? ` · ${blockout.notes}` : null}
        </div>
      </div>
      <button
        type="button"
        onClick={onDelete}
        disabled={busy}
        aria-label="Remove blockout"
        style={{
          padding: '0.25rem 0.6rem',
          background: 'transparent',
          color: EFIS.red,
          border: `1px solid ${EFIS.red}55`,
          borderRadius: '5px',
          fontFamily: EFIS.mono,
          fontSize: '0.72rem',
          fontWeight: 700,
          letterSpacing: '0.08em',
          cursor: busy ? 'not-allowed' : 'pointer',
          opacity: busy ? 0.5 : 1,
        }}
      >
        {busy ? '…' : 'CLR'}
      </button>
    </li>
  );
}

function AddBlockoutModal({ onClose, onAdded }) {
  const today = new Date().toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [reason, setReason] = useState('personal');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr(null);
    setSubmitting(true);
    try {
      await api.post('/api/admin/blockouts', {
        start_date: startDate,
        end_date: endDate,
        reason,
        notes: notes.trim() || undefined,
      });
      onAdded();
    } catch (e2) {
      setErr(e2?.response?.data?.error || e2?.message || 'Save failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-blockout-heading"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(2,5,8,0.78)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        zIndex: 9000,
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        style={{
          width: '100%',
          maxWidth: '420px',
          padding: '1.5rem',
          background: EFIS.panel,
          border: `1px solid ${EFIS.bezelHi}`,
          borderRadius: '12px',
          boxShadow: '0 18px 50px rgba(0,0,0,0.6), inset 0 0 40px rgba(0,0,0,0.5)',
          fontFamily: EFIS.mono,
          color: EFIS.white,
        }}
      >
        <h3
          id="add-blockout-heading"
          style={{
            margin: 0,
            marginBottom: '1rem',
            color: EFIS.cyan,
            letterSpacing: '0.16em',
            fontSize: '0.95rem',
          }}
        >
          FILE NOTAM — BLOCKOUT
        </h3>

        <label style={modalLabelStyle}>Start date</label>
        <input
          type="date"
          required
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          style={modalInputStyle}
        />

        <label style={modalLabelStyle}>End date</label>
        <input
          type="date"
          required
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          min={startDate}
          style={modalInputStyle}
        />

        <label style={modalLabelStyle}>Reason</label>
        <select
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          style={modalInputStyle}
        >
          {BLOCKOUT_REASONS.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>

        <label style={modalLabelStyle}>Notes (optional)</label>
        <input
          type="text"
          maxLength={120}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. Atlanta turn, back Friday"
          style={modalInputStyle}
        />

        {err ? (
          <div
            role="alert"
            style={{
              marginTop: '0.75rem',
              padding: '0.5rem 0.75rem',
              background: '#1a0e0e',
              color: EFIS.red,
              border: `1px solid ${EFIS.red}66`,
              borderRadius: '6px',
              fontSize: '0.82rem',
            }}
          >
            {err}
          </div>
        ) : null}

        <div
          style={{
            marginTop: '1.25rem',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.5rem',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={{
              padding: '0.5rem 0.9rem',
              background: 'transparent',
              color: EFIS.dim,
              border: `1px solid ${EFIS.bezelHi}`,
              borderRadius: '7px',
              fontFamily: EFIS.mono,
              fontSize: '0.8rem',
              fontWeight: 700,
              letterSpacing: '0.1em',
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >
            CANCEL
          </button>
          <button
            type="submit"
            disabled={submitting}
            style={{
              padding: '0.5rem 0.9rem',
              background: '#06212b',
              color: EFIS.cyan,
              border: `1px solid ${EFIS.cyan}88`,
              borderRadius: '7px',
              fontFamily: EFIS.mono,
              fontSize: '0.8rem',
              fontWeight: 700,
              letterSpacing: '0.1em',
              cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? 'FILING…' : 'FILE BLOCKOUT'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Helpers + shared styles
// ───────────────────────────────────────────────────────────────────────────
function formatDate(iso) {
  if (!iso) return '—';
  // iso is YYYY-MM-DD with no timezone. Treat it as local to avoid the
  // off-by-one-day bug from new Date('YYYY-MM-DD') being parsed as UTC.
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function formatTime(hhmmss) {
  if (!hhmmss) return '—';
  const [h, m] = hhmmss.split(':').map(Number);
  const dt = new Date();
  dt.setHours(h, m, 0, 0);
  return dt.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

const listStyle = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
};

const rowStyle = {
  padding: '0.85rem',
  background: EFIS.row,
  border: `1px solid ${EFIS.bezel}`,
  borderRadius: '7px',
};

const panelTextStyle = {
  margin: 0,
  fontSize: '0.86rem',
  lineHeight: 1.55,
  color: EFIS.dim,
  letterSpacing: '0.03em',
};

const linkStyle = {
  color: EFIS.cyan,
  textDecoration: 'none',
  borderBottom: `1px dotted ${EFIS.cyan}66`,
};

function rowActionStyle(color) {
  return {
    background: 'transparent',
    color,
    border: `1px solid ${color}66`,
    borderRadius: '5px',
    padding: '0.3rem 0.6rem',
    fontSize: '0.72rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    fontFamily: EFIS.mono,
  };
}

const rowInputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  background: EFIS.panel,
  border: `1px solid ${EFIS.bezel}`,
  borderRadius: '5px',
  color: EFIS.white,
  padding: '0.4rem 0.55rem',
  fontSize: '0.82rem',
  fontFamily: EFIS.mono,
};

const modalLabelStyle = {
  display: 'block',
  marginTop: '0.85rem',
  marginBottom: '0.3rem',
  fontWeight: 700,
  fontSize: '0.72rem',
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: EFIS.dim,
};

const modalInputStyle = {
  width: '100%',
  padding: '0.55rem 0.7rem',
  fontSize: '0.95rem',
  fontFamily: EFIS.mono,
  background: EFIS.row,
  color: EFIS.white,
  border: `1px solid ${EFIS.bezel}`,
  borderRadius: '6px',
  boxSizing: 'border-box',
};
